import { get, getIn } from 'immutable';
import { UserPermissions } from 'enums/user-permissions';
import { TeamMemberStatus } from 'enums/team-member-status';

const checkAll = (checkFuncs) => (user, args) =>
  checkFuncs.every((checkFunc) => checkFunc(user, args));

const checkMyCompany = (user, { companyId }) =>
  typeof companyId !== 'undefined' && companyId === getIn(user, ['company', 'company_id']);
const checkMyTeam = (user, { teamId }) =>
  typeof teamId !== 'undefined' && teamId === getIn(user, ['team', 'id']);
const checkNotSelf = (user, { email }) =>
  typeof email !== 'undefined' && email !== get(user, 'email');
const checkMemberStatusActive = (user, { teamMemberStatus }) =>
  typeof teamMemberStatus !== 'undefined' && teamMemberStatus === TeamMemberStatus.active;
const checkNotMemberChampion = (user, { teamMemberChampion }) =>
  typeof teamMemberChampion !== 'undefined' && !teamMemberChampion;
const checkMemberStatusPending = (user, { teamMemberStatus }) =>
  typeof teamMemberStatus !== 'undefined' && teamMemberStatus === TeamMemberStatus.pending;

const permissionsHandler = {
  [UserPermissions.canEditMyTeam]: checkMyTeam,
  [UserPermissions.canViewMyTeam]: checkMyTeam,
  [UserPermissions.canInviteTeam]: checkMyCompany,
  [UserPermissions.canInviteMembersToMyTeam]: checkMyTeam,
  [UserPermissions.canCreateNetworkManager]: checkNotSelf,
  [UserPermissions.canViewTeamInMyCompany]: checkMyCompany,
  [UserPermissions.canEditTeamsInMyCompany]: checkMyCompany,
  [UserPermissions.canInviteMembersToTeamsInMyCompany]: checkMyCompany,
  [UserPermissions.canEditMembersOnMyTeam]: checkAll([checkMyTeam, checkNotSelf]),
  [UserPermissions.canSelectTeamToMoveMembersToAnotherTeamOnMyCompany]: checkMyCompany,
  [UserPermissions.canEditMembersOnMyCompany]: checkAll([checkMyCompany, checkNotSelf]),
  [UserPermissions.canResendInviteOnMyTeam]: checkAll([checkMyTeam, checkMemberStatusPending]),
  [UserPermissions.canResendInviteOnMyCompany]: checkAll([
    checkMyCompany,
    checkMemberStatusPending,
  ]),
  [UserPermissions.canMakeChampionOnMyTeam]: checkAll([
    checkMyTeam,
    checkNotMemberChampion,
    checkMemberStatusActive,
  ]),
  [UserPermissions.canMakeChampionOnMyCompany]: checkAll([
    checkMyCompany,
    checkNotMemberChampion,
    checkMemberStatusActive,
  ]),
  [UserPermissions.canMoveMemberToAnotherTeamOnMyCompany]: checkAll([
    checkMyCompany,
    checkNotSelf,
    checkMemberStatusActive,
    checkNotMemberChampion,
  ]),
};

export const hasOneOfPermissions = (permissions, targets) =>
  !!targets.find((permission) => permissions.indexOf(permission) >= 0);

/**
 * @name existsPermission
 * @description
 * Method that checks if a target permission exists on the permissions array
 */
const existsPermission = (permissions = [], target) => permissions.indexOf(target) >= 0;

/**
 * @name hasPermission
 * @description
 * Method that evaluate if the target permission exists on the permission list
 * and runs custom permission checks if needed.
 *
 * @param {Array<string>} permissions Permission list to check
 * @param {string} target Target permission that will be evaluated
 * @param {object} user User calling the method
 * @param {object} args Arguments used to check custom permissions
 */
const hasPermission = (permissions, target, user, args, options = {}) => {
  if (!existsPermission(permissions, target)) {
    return false;
  }

  if (options.skipHandlers) {
    return true;
  }

  if (user && existsPermission(Object.keys(permissionsHandler), target)) {
    return permissionsHandler[target](user, args);
  }

  return true;
};

/**
 * @name canAccess
 * @description
 * Method that checks if a user can access a particular area by checking is permissions.
 * @param {Array<string>} permissions The users permissions to check
 * @param {string | Array<string>} target The target permission(s) to validate.
 *                   If an array is provided an OR operation will be triggered
 * @param {object} user User calling the method
 * @param {object} args Arguments used to check custom permissions
 * @param {object} options Valid options are:
 *                  - skipHandlers: Allows skipping the custom permission handlers checks.
 *                                  Only use if you are absolutely sure
 */
export const canAccess = (permissions, target, user, args = {}, options = {}) => {
  if (!target) {
    return false;
  }

  if (!Array.isArray(target)) {
    return hasPermission(permissions, target, user, args, options);
  }

  return target.map((tg) => hasPermission(permissions, tg, user, args, options)).includes(true);
};

export default permissionsHandler;
